import type { TrailBucket, TrailStepRecord } from '@/components/assistant-ui/thread/trail-records';
import { pickInSequence } from '@/lib/utils/pick-by-hash';

/** Swallows the throw so callers can fall through to a static fallback (next-intl throws on missing keys in strict mode). */
export function tryCall<T>(fn: () => T | undefined): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

/** Minimal next-intl translator contract — function + optional `raw` for array-valued keys. */
type Translator = ((key: string, values?: Record<string, string | number>) => string) & {
  raw?: (k: string) => unknown;
};

/** Returns `fallback` when `t(key)` echoes the key (next-intl's lenient-mode "missing key" signal). */
export function safeT({
  t,
  key,
  fallback,
}: {
  t: (key: string) => string;
  key: string;
  fallback: string;
}): string {
  const value = t(key);
  return value === key ? fallback : value;
}

export function readVariantsFromTranslations({
  t,
  key,
}: {
  t: Translator;
  key: string;
}): readonly string[] | undefined {
  try {
    const raw = t.raw?.(key);
    if (Array.isArray(raw) && raw.length > 0 && raw.every(v => typeof v === 'string')) {
      return raw as readonly string[];
    }
  } catch {
    // raw throws if the key is missing — fall through
  }
  return undefined;
}

export function resolveVariants({
  override,
  t,
  variantsKey,
  fallbackKey,
  fallbackText,
}: {
  override: string | readonly string[] | undefined;
  t: Translator;
  variantsKey: string;
  /** Optional singular-key fallback for callers that still keep one in messages. */
  fallbackKey?: string;
  fallbackText?: string;
}): readonly string[] {
  if (Array.isArray(override) && override.length > 0) return override;
  if (typeof override === 'string' && override.length > 0) return [override];
  const fromCatalog = readVariantsFromTranslations({ t, key: variantsKey });
  if (fromCatalog) return fromCatalog;
  if (!fallbackKey) return fallbackText ? [fallbackText] : [];
  const single = safeT({ t, key: fallbackKey, fallback: fallbackText ?? '' });
  return single ? [single] : [];
}

export interface TrailVariantOverrides {
  preparingText?: string | readonly string[];
  streamingText?: string | readonly string[];
  longWaitText?: string | readonly string[];
  /**
   * Per-bucket overrides for the **final** receipt copy only. Intermediate
   * gap / streaming rows are intentionally bucket-agnostic — the model's
   * mid-stream text isn't guaranteed to relate to the prior tool, so
   * generic copy reads more honestly. The closer is bucket-aware because
   * it summarizes work that's already happened.
   */
  streamingDoneByBucket?: Partial<Record<TrailBucket, readonly string[]>>;
  /**
   * Free-form bag of tenant-specific final labels keyed by application-defined
   * names (e.g. `flightsAndVacations`, `flightsOnlyVacationsEmpty`). Read by
   * the tenant's `resolveDoneLabel` to compose multi-tool / empty-results /
   * count-aware closers without polluting shared LoadingUI translation files.
   */
  doneLabels?: Record<string, string>;
}

export interface TrailVariants {
  streaming: readonly string[];
  preparing: readonly string[];
  longWait: readonly string[];
}

/** Resolves the streaming / preparing / longWait variant arrays from overrides → catalog → fallback. */
export function resolveTrailVariants({
  overrides,
  t,
}: {
  overrides: TrailVariantOverrides | undefined;
  t: Translator;
}): TrailVariants {
  const streaming = resolveVariants({
    override: overrides?.streamingText,
    t,
    variantsKey: 'activityTrail.streamingVariants',
  });
  const preparing = resolveVariants({
    override: overrides?.preparingText,
    t,
    variantsKey: 'activityTrail.preparingVariants',
    fallbackText: t('rotatingToolUI.loading'),
  });
  const longWait = resolveVariants({
    override: overrides?.longWaitText,
    t,
    variantsKey: 'activityTrail.preparingLongVariants',
  });
  return { streaming, preparing, longWait };
}

export interface TrailBucketCatalog {
  t: Translator;
  namespace: string;
}

export function pickStreamingDoneForBucket({
  bucket,
  fallbackSingle,
  overrides,
  catalog,
}: {
  bucket: TrailBucket;
  fallbackSingle: string;
  overrides?: TrailVariantOverrides;
  catalog?: TrailBucketCatalog;
}): readonly string[] {
  if (bucket === 'cold') return fallbackSingle ? [fallbackSingle] : [];
  const fromOverride = overrides?.streamingDoneByBucket?.[bucket];
  if (fromOverride && fromOverride.length > 0) return fromOverride;
  if (catalog) {
    const fromCatalog = readVariantsFromTranslations({
      t: catalog.t,
      key: `${catalog.namespace}.${bucket}`,
    });
    if (fromCatalog) return fromCatalog;
  }
  return fallbackSingle ? [fallbackSingle] : [];
}

type ResolveDoneLabelFn = (ctx: {
  records: readonly TrailStepRecord[];
  t: Translator;
  trailConfig?: TrailVariantOverrides;
}) => string | undefined;

/** Composes the final-row label resolver used by both the live runtime and
 *  the synthetic ActivityTrail story. Centralizing the chain — tenant
 *  resolver → bucket-keyed catalog → flat fallback — keeps the story from
 *  drifting from production. */
export function buildPickFinal({
  resolveDoneLabel,
  stepRecords,
  t,
  fallbackFinalSingle,
  trailOverrides,
  catalogNamespace,
}: {
  resolveDoneLabel: ResolveDoneLabelFn | undefined;
  stepRecords: readonly TrailStepRecord[];
  t: Translator;
  fallbackFinalSingle: string;
  trailOverrides: TrailVariantOverrides | undefined;
  catalogNamespace?: string;
}): (bucket: TrailBucket) => string | undefined {
  const catalog = catalogNamespace ? { t, namespace: catalogNamespace } : undefined;
  return bucket => {
    const fromResolver = resolveDoneLabel?.({
      records: stepRecords,
      t,
      trailConfig: trailOverrides,
    });
    if (fromResolver) return fromResolver;
    const items = pickStreamingDoneForBucket({
      bucket,
      fallbackSingle: fallbackFinalSingle,
      overrides: trailOverrides,
      catalog,
    });
    if (items.length === 0) return undefined;
    return pickInSequence({ items, seed: `final:${bucket}`, index: 0 });
  };
}
